import React, { useEffect, useMemo, useState } from 'react'
import { Box, IconButton, InputAdornment, TextField, useMediaQuery, useTheme } from '@mui/material'
import { IconArrowRight, IconSearch } from '@tabler/icons-react'
import { Card, ContentTitle, Status, TableApiV2, TableHeaderApi } from '~/components'
import { FilterPartnerUserDrawer } from './components/FilterPartnerUserDrawer'
import { useNavigate } from 'react-router-dom'
import { SearchQueysData, useDebounce, useQueryString, useToggle } from '~/hooks'
import { colorAffiliatePartnerUserStatus, displayCpfCnpj, formatDate } from '~/utils'
import { EAffiliatePartnerUserStatus, usePartnerAffiliateUserListQuery } from '~/graphql/types'
import { Column, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'

export const PartnerUserList: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle(false)

  const queryKeys: SearchQueysData[] = [
    { key: 'status', defaultValue: [], parseValue: true },
    { key: 'nameOrEmailOrCpf', defaultValue: '' },
    { key: 'partnerId', defaultValue: '' },
  ]

  const { searchQuerys, setSearchParams, page, size } = useQueryString()
  const queryValues = searchQuerys(queryKeys)

  const isLowerlg = useMediaQuery(theme.breakpoints.down('xl'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [filterName, setFilterName] = useState(queryValues.nameOrEmailOrCpf)
  const debouncedFilterName = useDebounce(filterName, 1000)

  useEffect(() => {
    setSearchParams(state => {
      if (debouncedFilterName) {
        state.set('nameOrEmailOrCpf', debouncedFilterName)
        state.set('page', '0')
      } else {
        state.delete('nameOrEmailOrCpf')
      }
      return state
    })
  }, [debouncedFilterName])

  const { data, loading } = usePartnerAffiliateUserListQuery({
    variables: {
      params: {
        pageSize: size,
        pageNumber: page,
        sort: {
          field: 'createdAt',
          order: -1
        },
        ...queryValues?.nameOrEmailOrCpf?.length > 0 ? { nameOrEmailOrCpf: queryValues.nameOrEmailOrCpf } : {},
        ...queryValues?.status?.length > 0 ? { inStatus: queryValues.status as EAffiliatePartnerUserStatus[] } : {},
        ...queryValues?.partnerId?.length > 0 ? { partnerId: queryValues.partnerId } : {},
      }
    },
    onCompleted(result) {
      setSearchParams(state => {
        state.set('items', String(result.partnerAffiliateUserList.meta.documentCounts))
        return state
      })
    },
    fetchPolicy: 'no-cache'
  })

  const columns = useMemo((): Column[] => {
    return [
      {
        Header: 'Nome',
        accessor: 'name',
      },
      {
        Header: 'CPF',
        accessor: 'cpf',
        Cell: (props) => <>{props.value ? displayCpfCnpj(props.value) : '—'}</>,
        disableSortBy: true,
      },
      {
        Header: 'E-mail',
        accessor: 'email',
        disableSortBy: true,
      },
      {
        Header: 'Representante',
        accessor: 'partnerRef.name',
        disableSortBy: true,
        Cell: (props) => <>{props.value || '—'}</>,
      },
      {
        Header: 'Cadastrado em',
        accessor: 'createdAt',
        Cell: (props) => <>{formatDate(props.value)}</>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: (props) => {
          const statusMap: Record<string, string> = {
            [EAffiliatePartnerUserStatus.underAnalysis]: 'Em análise',
            [EAffiliatePartnerUserStatus.approved]: 'Aprovado',
            [EAffiliatePartnerUserStatus.rejected]: 'Reprovado',
          }
          const colors = colorAffiliatePartnerUserStatus(props.value)
          return <Status background={colors.background} text={statusMap[props.value] || props.value} color={colors.color} />
        }
      },
      {
        Header: 'Ações',
        accessor: '_id',
        disableSortBy: true,
        Cell: (props) => (
          <IconButton onClick={() => navigate(`/app/partners/users/${props.value}`)}>
            <IconArrowRight />
          </IconButton>
        )
      },
    ]
  }, [theme, navigate])

  const dataWithMemo = useMemo(() => data?.partnerAffiliateUserList?.data || [], [data])

  const defaultColumnHiddens = isLowerlg ? ['createdAt'].concat(isLowerMd ? ['email'] : []).concat(isLowerSm ? ['cpf'] : []) : []

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    defaultColumn: {
      Filter: <></>
    },
    manualPagination: true,
    initialState: {
      hiddenColumns: defaultColumnHiddens
    },
  },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  )

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem 1rem 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title='Prepostos cadastrados'
          description='Visualize e gerencie os prepostos dos representantes comerciais'
          breadcrumbLinks={{ currentLink: 'Prepostos', links: [{ href: '/app/partners', label: 'Representantes' }] }}
        />

        <TableHeaderApi
          startElement={(
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%', flexFlow: isLowerSm ? 'column-reverse' : 'row' }}>
              <TextField
                fullWidth
                sx={{ maxWidth: '400px' }}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder='Buscar nome, e-mail ou CPF...'
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment position='end'>
                        <IconSearch />
                      </InputAdornment>
                    </>
                  )
                }}
              />
              <FilterPartnerUserDrawer queryKeys={queryKeys} drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} toggleDrawer={toggleDrawer} />
            </Box>
          )}
        />
        <TableApiV2
          isLoading={loading}
          tableMethods={tableMethods}
          columns={columns}
        />
      </Card>
    </div>
  )
}
